import { useState } from 'react';
import { Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input, Alert, Logo } from '@heroui/react';
import Field from '../../components/common/Field';
import { authApi } from '../../api/authApi';
import { useUiStore } from '../../stores/uiStore';

/**
 * Phase 27a — public forgot-password page. Posts the email to the
 * /auth/forgot-password endpoint and shows the same success message
 * regardless of whether the email matched a user (anti-enumeration).
 *
 * Once the user submits, we never reveal whether the email matched —
 * users with valid emails will receive a reset link via SendGrid;
 * users without will see the same "we sent it if it exists" message.
 */

const forgotSchema = z.object({
  email: z.string().email('Enter a valid email address'),
});

type ForgotFormData = z.infer<typeof forgotSchema>;

export default function ForgotPasswordPage() {
  const theme = useUiStore((s) => s.resolvedTheme);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotFormData>({ resolver: zodResolver(forgotSchema) });

  const onSubmit = async (data: ForgotFormData) => {
    setError(null);
    try {
      await authApi.forgotPassword(data.email.trim());
      setSubmitted(true);
    } catch (err: unknown) {
      // Server always returns 200, so this branch only fires on real
      // network errors. Show a generic retry hint without leaking info.
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setError(message || 'Something went wrong. Try again in a moment.');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-default-50">
      <div className="w-full max-w-sm space-y-6 p-6">
        <div className="flex flex-col items-center space-y-3">
          <Logo variant="stacked" size={140} onDark={theme === 'dark'} />
          <h1 className="text-default-500 text-sm font-normal">Reset your password</h1>
        </div>

        {submitted ? (
          <div className="space-y-4">
            <Alert status="success">
              If an account matches that email, we've sent a password reset link.
              Check your inbox + spam folder. The link expires in 30 minutes.
            </Alert>
            <p className="text-center text-sm text-default-500">
              <Link to="/login" className="text-primary font-medium">
                Back to sign in
              </Link>
            </p>
          </div>
        ) : (
          <>
            <p className="text-center text-sm text-default-500">
              Enter your email and we'll send you a link to set a new password.
            </p>

            {error && <Alert status="danger">{error}</Alert>}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <Field label="Email" isRequired error={errors.email?.message}>
                <Input
                  type="email"
                  aria-label="Email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  autoFocus
                  fullWidth
                  {...register('email')}
                />
              </Field>

              <Button
                type="submit"
                style={{
                  width: '100%',
                  backgroundColor: '#0D1B2A',
                  color: '#F0D060',
                  borderColor: '#0D1B2A',
                  opacity: isSubmitting ? 0.7 : 1,
                }}
                isDisabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send reset link'}
              </Button>

              <p className="text-center text-sm text-default-500">
                Remembered it?{' '}
                <Link to="/login" className="text-primary font-medium">
                  Back to sign in
                </Link>
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
